import { IAnalyticsTag } from './analyticsElements';
/*
 * Dictionary of strategies that can be utilized to scrape the page of values
 * to be used for analytics
 *
 * Each strategy will receive an element and return a value
 *
 * Default Strategy getElementValue
 *
 * Example to get value for sectionId.
 * From Selector, we get DOM with id courseSelect, we check here to do we have a strategy to get its value
 * using same selector.
 *
 * Example:
 * <select id='courseSelect' value='section1,course1'>
 *   <option value='section1,course1'> Course 1 </option>
 *   <option value='section2,course2'> Course 2 </option>
 *   <option value='section3,course3'> Course 3 </option>
 * </select
 * <button data-analytics='student-course-click' />
 *
 * In File: analyticsElements.ts
 * {
 *   eventAction   : "courseClick",
 *   eventCategory : "Course",
 *   tags: [{
 *           name:sectionId
 *           selector: "[id='courseSelect']"
 *           }
 *       ]
 * }
 *
 * In File: analyticsTagParsers.ts
 * "[id='courseSelect']":(element: HTMLInputElement) => {
 *       const selectValue = element.value;
 *       const sectionId: string = selectValue ? selectValue.substring(selectValue.indexOf(',') + 1) : '';
 *       return sectionId;
 *   }
 *
 * */

export interface IAnalyticsTagValue {
    key: string;
    value: string | number;
}

interface IAnalyticsTagValueGetterHash {
    [key: string]: (element: HTMLInputElement) => string;
}

const tagValueGetters: IAnalyticsTagValueGetterHash = {
    "[id='courseSelect']": (element: HTMLInputElement) => {
        const selectValue = element.value;
        const sectionId: string = selectValue
            ? selectValue.substring(selectValue.indexOf(',') + 1)
            : '';
        return sectionId;
    },
    "[id='quickSearchDropDownButton']": (element: HTMLInputElement) => element.textContent,
};

const getElementValue = (element: HTMLInputElement): string => {
    return element.value;
};

const getFromTargetElement = (
    tagName: string,
    targetElement: HTMLElement,
): IAnalyticsTagValue => {
    if (!targetElement) {
        return null;
    }

    const tagValue = targetElement.dataset[tagName];
    if (tagValue !== undefined) {
        return {
            key: tagName,
            value: tagValue,
        };
    }
    return null;
};

const getFromTagSelector = (name: string, selector: string) => {
    const taggedElement: HTMLInputElement = document.querySelector(selector);
    if (!taggedElement) {
        return null;
    }

    const getter = tagValueGetters[selector];
    const tagValue: string = getter
        ? getter(taggedElement)
        : getElementValue(taggedElement);
    if (tagValue !== undefined) {
        return {
            key: name,
            value: tagValue,
        };
    }
    return null;
};

const getFromHiddenElement = (name: string): IAnalyticsTagValue => {
    const taggedElement: HTMLInputElement = document.getElementById(
        'wa-analytics-' + name,
    ) as HTMLInputElement;
    if (!taggedElement) {
        return null;
    }

    const tagValue: string = getElementValue(taggedElement);
    if (tagValue !== undefined) {
        return {
            key: name,
            value: tagValue,
        };
    }
    return null;
};

/*
 *
 * Example 1 (getFromTargetElement):
 * <a data-analytics='student-assignment-click' data-assignment-id='assignment1'/>
 * <a data-analytics='student-assignment-click' data-assignment-id='assignment2'/>
 * <a data-analytics='student-assignment-click' data-assignment-id='assignment3'/>
 *
 * In File: analyticsElements.ts
 * {
 *   eventAction   : "courseClick",
 *   eventCategory : "Course",
 *   tags: [{
 *           name:assignmentId
 *           }
 *       ]
 * }
 *
 * In this case, we check in targetElement dataset with the tag name assignmentId
 *
 *  Example 2: (getFromTagSelector)
 * <button data-analytics='student-section-click' />
 * <input id='courseSelect' value='sectionId'/>
 *
 * In File: analyticsElements.ts
 * {
 *   eventAction   : "sectionClick",
 *   eventCategory : "Section",
 *   tags: [{
 *           name:sectionId
 *           selector: "[id='courseSelect']"
 *           }
 *       ]
 * }
 *
 * In this case targetElement don't have a dataset with tag name sectionId,
 * So we get the value from the provided selector
 *
 *  Example 3: (getFromHiddenElement)
 * <button data-analytics='student-section-click' />
 * <input type='hidden' id='wa-analytics-sectionId' value='sectionId'/>
 *
 * In File: analyticsElements.ts
 * {
 *   eventAction   : "sectionClick",
 *   eventCategory : "Section",
 *   tags: [{
 *           name:sectionId
 *           }
 *       ]
 * }
 *
 * In this case targetElement don't have a dataset with tag name sectionId,
 * and we don't have selector, So we get the value from the hidden element
 * prefixed dataset "wa-analytics-{tagName}"
 * */
const getTagValue = (
    tag: IAnalyticsTag,
    targetElement: HTMLElement = null,
): IAnalyticsTagValue => {
    const { name, selector } = tag;

    // 1 - check for tag value in event Initiating DOM
    let keyValue: IAnalyticsTagValue = getFromTargetElement(
        name,
        targetElement,
    );

    // 2 - get from selector
    if (!keyValue) {
        keyValue = getFromTagSelector(name, selector);
    }

    // 3 - get from hidden element
    if (!keyValue) {
        keyValue = getFromHiddenElement(name);
    }
    return keyValue;
};

export { getTagValue };
